import React, { useState, useEffect } from 'react';
import axios from 'axios';

function CentralFileUploader({ projectId, onFileUploaded }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [steps, setSteps] = useState([]);
  const [selectedStepId, setSelectedStepId] = useState('');
  
  // Fetch steps for the project when projectId changes
  useEffect(() => {
    if (!projectId) return;
    
    const fetchSteps = async () => {
      try {
        const response = await axios.get(`/api/client-portal/projects/${projectId}`);
        if (response.data && response.data.project && response.data.project.phases) {
          // Flatten all steps from all phases
          const allSteps = [];
          response.data.project.phases.forEach(phase => {
            if (phase.steps && phase.steps.length > 0) {
              phase.steps.forEach(step => {
                allSteps.push({
                  ...step,
                  phase_name: phase.name
                });
              });
            }
          });
          setSteps(allSteps);
          
          // Preselect the first step if available
          if (allSteps.length > 0) {
            setSelectedStepId(allSteps[0].id);
          }
        }
      } catch (error) {
        console.error('Error fetching steps for file upload:', error);
        setErrorMessage('Failed to load steps. Please refresh the page.');
      }
    };
    
    fetchSteps();
  }, [projectId]);
  
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Reset any previous errors
      setErrorMessage('');
      
      // Check file size limit (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setErrorMessage('File size exceeds the 10MB limit.');
        return;
      }
      
      setSelectedFile(file);
    }
  };
  
  const handleUpload = async () => {
    if (!selectedFile || !selectedStepId) {
      setErrorMessage('Please select a file and a step.');
      return;
    }
    
    try {
      setUploading(true);
      setUploadProgress(0);
      
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      console.log('Uploading file to step:', selectedStepId);
      
      const response = await axios.post(
        `/api/client-portal/files/upload/${selectedStepId}`, 
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        }
      );
      
      if (response.data && response.data.file) {
        // Notify parent component about the uploaded file
        if (onFileUploaded) {
          onFileUploaded(response.data.file);
        }
        
        // Reset the file input
        setSelectedFile(null);
        
        // Show success message
        alert('File uploaded successfully');
      }
    } catch (error) {
      console.error('File upload error:', error);
      console.error('File upload error response:', error.response?.data);
      console.error('File upload error status:', error.response?.status);
      setErrorMessage(error.response?.data?.error || 'Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };
  
  return (
    <div className="p-4 bg-white rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-lg font-medium text-gray-900 mb-3">Upload Files</h3>
      
      {/* Step selector */}
      <div className="mb-3">
        <label htmlFor="step-select" className="block text-sm font-medium text-gray-700 mb-1">
          Select a step to upload to
        </label>
        <select
          id="step-select"
          value={selectedStepId}
          onChange={(e) => setSelectedStepId(e.target.value)}
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          disabled={uploading || steps.length === 0}
        >
          {steps.length === 0 ? (
            <option value="">No steps available</option>
          ) : (
            steps.map(step => (
              <option key={step.id} value={step.id}>
                {step.phase_name} - {step.name}
              </option>
            ))
          )}
        </select>
      </div>
      
      {/* File selector and upload button */}
      <div className="flex items-center space-x-2">
        <label className="flex-1">
          <input
            type="file"
            onChange={handleFileChange}
            disabled={uploading || !selectedStepId}
            className="hidden"
          />
          <div className={`px-4 py-2 text-sm border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 text-center truncate ${(!selectedStepId) ? 'opacity-50 cursor-not-allowed' : ''}`}>
            {selectedFile ? selectedFile.name : 'Choose File'}
          </div>
        </label>
        <button
          onClick={handleUpload}
          disabled={!selectedFile || uploading || !selectedStepId}
          className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
        >
          {uploading ? 'Uploading...' : 'Upload'}
        </button>
      </div>
      
      {/* Progress bar during upload */}
      {uploading && (
        <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
          <div 
            className="bg-blue-600 h-2.5 rounded-full" 
            style={{ width: `${uploadProgress}%` }}
          ></div>
        </div>
      )}
      
      {/* Error message */}
      {errorMessage && (
        <p className="mt-2 text-sm text-red-600">
          {errorMessage}
        </p>
      )}
    </div>
  );
}

export default CentralFileUploader; 